import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Download, Users, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscribersTab = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [customerEmails, setCustomerEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeList, setActiveList] = useState('newsletter');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch newsletter subscribers
      const newsletterRes = await axios.get(`${API}/admin/newsletter/subscribers`);
      setSubscribers(newsletterRes.data.subscribers || []);
      
      // Fetch customer emails from orders
      const ordersRes = await axios.get(`${API}/admin/orders`);
      const orders = ordersRes.data || [];
      
      // Extract unique customer emails from orders
      const emailMap = new Map();
      orders.forEach(order => {
        const email = order.customer_email;
        if (email && !emailMap.has(email)) {
          emailMap.set(email, {
            email,
            name: order.customer_name ? 
              `${order.customer_name.first_name || ''} ${order.customer_name.last_name || ''}`.trim() : '',
            phone: order.phone || '',
            order_count: 1,
            last_order: order.created_at
          });
        } else if (email) {
          const existing = emailMap.get(email);
          existing.order_count++;
          if (order.created_at > existing.last_order) {
            existing.last_order = order.created_at;
          }
        }
      });
      setCustomerEmails(Array.from(emailMap.values()));
    } catch (error) {
      toast.error('Failed to load subscriber data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const exportToCSV = (type) => {
    let data, filename, headers;
    
    if (type === 'newsletter') {
      headers = ['Email', 'First Name', 'Subscribed Date'];
      data = subscribers.map(s => [
        s.email,
        s.first_name || '',
        s.created_at ? new Date(s.created_at).toLocaleDateString() : ''
      ]);
      filename = 'newsletter_subscribers.csv';
    } else {
      headers = ['Email', 'Name', 'Phone', 'Orders', 'Last Order'];
      data = customerEmails.map(c => [
        c.email,
        c.name,
        c.phone,
        c.order_count,
        c.last_order ? new Date(c.last_order).toLocaleDateString() : ''
      ]);
      filename = 'customer_emails.csv';
    }
    
    const csvContent = [
      headers.join(','),
      ...data.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success(`Exported ${type === 'newsletter' ? subscribers.length : customerEmails.length} emails`);
  };

  const copyAllEmails = (type) => {
    const emails = type === 'newsletter' 
      ? subscribers.map(s => s.email)
      : customerEmails.map(c => c.email);
    
    navigator.clipboard.writeText(emails.join('\n'));
    toast.success(`Copied ${emails.length} emails to clipboard`);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-20 bg-sand rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscribers-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          className={`bg-white rounded-2xl shadow-card p-6 cursor-pointer transition-all ${activeList === 'newsletter' ? 'ring-2 ring-terracotta' : ''}`}
          onClick={() => setActiveList('newsletter')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-muted text-sm">Newsletter Subscribers</p>
              <p className="font-heading text-3xl text-terracotta">{subscribers.length}</p>
            </div>
            <Mail className="w-10 h-10 text-terracotta/30" />
          </div>
        </div>
        
        <div 
          className={`bg-white rounded-2xl shadow-card p-6 cursor-pointer transition-all ${activeList === 'customers' ? 'ring-2 ring-forest' : ''}`}
          onClick={() => setActiveList('customers')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-muted text-sm">Customer Emails (from orders)</p>
              <p className="font-heading text-3xl text-forest">{customerEmails.length}</p>
            </div>
            <Users className="w-10 h-10 text-forest/30" />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <Button 
          onClick={() => exportToCSV(activeList)}
          className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white"
        >
          <Download className="w-4 h-4 mr-2" />
          Export {activeList === 'newsletter' ? 'Newsletter' : 'Customers'} CSV
        </Button>
        <Button 
          variant="outline"
          onClick={() => copyAllEmails(activeList)}
          className="rounded-full"
        >
          <Mail className="w-4 h-4 mr-2" />
          Copy All Emails
        </Button>
        <Button 
          variant="outline"
          onClick={fetchData}
          className="rounded-full"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Email List */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="p-4 border-b border-sand">
          <h3 className="font-heading text-lg text-text-primary">
            {activeList === 'newsletter' ? 'Newsletter Subscribers' : 'Customer Emails'}
          </h3>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {activeList === 'newsletter' ? (
            subscribers.length === 0 ? (
              <div className="p-8 text-center text-text-muted">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No newsletter subscribers yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-sand/50 sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-text-secondary">Email</th>
                    <th className="text-left p-3 text-sm font-medium text-text-secondary">Name</th>
                    <th className="text-left p-3 text-sm font-medium text-text-secondary">Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub) => (
                    <tr key={sub.email} className="border-b border-sand/50 hover:bg-sand/30">
                      <td className="p-3 text-sm">{sub.email}</td>
                      <td className="p-3 text-sm text-text-muted">{sub.first_name || '-'}</td>
                      <td className="p-3 text-sm text-text-muted">
                        {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            customerEmails.length === 0 ? (
              <div className="p-8 text-center text-text-muted">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No customer orders yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-sand/50 sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-text-secondary">Email</th>
                    <th className="text-left p-3 text-sm font-medium text-text-secondary">Name</th>
                    <th className="text-left p-3 text-sm font-medium text-text-secondary">Phone</th>
                    <th className="text-left p-3 text-sm font-medium text-text-secondary">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {customerEmails.map((customer) => (
                    <tr key={customer.email} className="border-b border-sand/50 hover:bg-sand/30">
                      <td className="p-3 text-sm">{customer.email}</td>
                      <td className="p-3 text-sm text-text-muted">{customer.name || '-'}</td>
                      <td className="p-3 text-sm text-text-muted">{customer.phone || '-'}</td>
                      <td className="p-3 text-sm text-text-muted">{customer.order_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscribersTab;
